import { CloseOutlined } from '@ant-design/icons';
import { Tag } from 'antd';
import * as Draft from 'draft-js';
import * as React from 'react';

import { EditorEntityKind } from '../../editorEntityKind';

import s from './FormulaFieldEntityDecorator.module.scss';

type Props = React.PropsWithChildren<{
  contentState: Draft.ContentState;
  entityKey: string;
  offsetKey: string;
}>;

/**
 * Функция-стратегия позволяет из сплошного текста вычислить границы отрезков,
 * для которых надо применить текущий декоратор
 *
 * @param contentBlock - блок, над которым применяется декоратор
 * @param callback - вызывается с координатами текста start end, для которых надо применить декоратор
 * @param contentState — состояние редактора содержит информацию о сущностях в
 */
const formFieldStrategy = (
  contentBlock: Draft.ContentBlock,
  callback: (startIndex: number, endIndex: number) => void,
  contentState: Draft.ContentState
) => {
  /**
   * Находим только координаты сущностей типа EditorEntityKind.formField
   */
  contentBlock.findEntityRanges((meta) => {
    const entityKey = meta.getEntity();

    if (!entityKey) {
      return false;
    }

    const entityInstance = contentState.getEntity(entityKey);

    return entityInstance.getType() === EditorEntityKind.formField;
  }, callback);
};

const View: React.FC<Props> = ({
  entityKey,
  contentState,
  offsetKey,
  children,
}: Props) => {
  const handleClose = React.useCallback(() => {
    if (!entityKey) {
      return null;
    }

    const { onRemoveEntity } = contentState.getEntity(entityKey).getData();

    onRemoveEntity?.(entityKey);
  }, [contentState, entityKey]);

  return (
    <Tag color="blue" className={s.view} data-offset-key={offsetKey}>
      {children}
      {/* Добавляем крестик так, а не через пропс closable у тега,
      потому что если передать closable, то при закрытии тег демонтируется самостоятельно,
      без синхронизации со внешним состоянием. Это ломает редактор.
      Делаем контролируемое удаление тега собственноручно  */}
      <CloseOutlined onClick={handleClose} />
    </Tag>
  );
};

/**
 * Декоратор FormulaFieldEntity рисует визуал antd-тега вокруг ссылки на поле таблицы
 */
export const FormulaFieldEntityDecorator: Draft.DraftDecorator = {
  strategy: formFieldStrategy,
  component: View,
};
